/**
 * La Sura cookie banner (sections/lasura-cookie-banner.liquid): when to show, the
 * preferences, the choice. The dog (lasura-cookie-dog.js) only for those yet to choose.
 * A link to #cookie-settings, or [data-lasura-cookie-settings], reopens the preferences.
 */
const CATEGORIES = ['preferences', 'analytics', 'marketing'];
const WAG_BY_CHOICES = ['0.9s', '0.5s', '0.3s', '0.16s'];
const WALK_OFF_LIMIT = 4000;
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const CHOSEN = 'lasura-consent-chosen';
const titleCase = (word) => word[0].toUpperCase() + word.slice(1);

/** Shopify's Customer Privacy API, or null; never its early stub, which has no
 *  setTrackingConsent (2026-10-09). */
function loadPrivacyApi() {
  return new Promise((resolve) => {
    const api = () => (typeof window.Shopify?.customerPrivacy?.currentVisitorConsent === 'function' ? window.Shopify.customerPrivacy : null);
    if (api()) return resolve(api());
    if (!window.Shopify?.loadFeatures) return resolve(null);
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], async (error) => {
      for (let i = 0; !error && !api() && i < 40; i++) await wait(100);
      resolve(error ? null : api());
    });
  });
}

class LasuraCookieBanner extends HTMLElement {
  connectedCallback() {
    const $ = (selector) => this.querySelector(selector);
    this.wrap = $('[data-wrap]');
    this.card = $('[data-card]');
    this.dog = $('[data-dog]');
    this.walker = $('[data-walker]');
    this.quip = $('[data-quip]');
    this.prefs = $('[data-prefs]');
    this.prefsDog = $('[data-prefs-dog]');
    this.toastEl = $('[data-toast]');
    this.switches = [...this.querySelectorAll('[data-category]')];
    this.designMode = Boolean(window.Shopify?.designMode);

    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.addEventListener('click', (event) => this.onClick(event));
    document.addEventListener('click', this.onDocumentClick);
    this.prefs.addEventListener('close', () => this.opener?.focus({ preventScroll: true }));
    // Backdrop click.
    this.prefs.addEventListener('click', (event) => event.target === this.prefs && this.prefs.close());
    this.switches.forEach((input) => input.addEventListener('change', () => this.wagByChoices()));
    // Hover for mouse and pen only: on iOS a tap revealing the quip counts as hover and drops the click.
    this.querySelectorAll('[data-mood]').forEach((button) => {
      const on = () => this.mood(button.dataset.mood, button.dataset.quip);
      const off = () => !this.busy && this.mood(null);
      const byPointer = (handler) => (event) => event.pointerType !== 'touch' && handler();
      button.addEventListener('pointerenter', byPointer(on));
      button.addEventListener('focus', on);
      button.addEventListener('pointerleave', byPointer(off));
      button.addEventListener('blur', off);
    });
    // Editor: selecting the section shows it.
    this.onSectionSelect = (event) => event.target.contains(this) && this.wrap.hidden && this.show(false);
    document.addEventListener('shopify:section:select', this.onSectionSelect);
    this.start();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('shopify:section:select', this.onSectionSelect);
    this.clearScrollPadding();
  }

  /** Only when the dog shows (not on short landscape phones). */
  get animated() {
    return Boolean(this.dog) && getComputedStyle(this.dog).display !== 'none' &&
      this.dataset.animate === 'true' && !matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  dogModule() {
    this.dogImport ||= import(new URL(this.dataset.dogModule, location.href).href);
    return this.dogImport;
  }

  async start() {
    // Fetch the dog with the API; show the banner as it answers (Lighthouse, 2026-10-07).
    if (this.animated && !this.designMode && this.mayShow()) this.dogModule().catch(() => {});
    this.api = await loadPrivacyApi();
    if (location.hash === '#cookie-settings') this.openPreferences(null);
    if (this.designMode) return this.show(false);
    if (!this.api || !this.shouldShow()) return void (this.dataset.noAsk = '');
    requestAnimationFrame(() => this.show(this.animated));
  }

  /** Not chosen in 11 months (Safari's API can report no choice on the next page). */
  mayShow() {
    try {
      return !(Date.now() - localStorage.getItem(CHOSEN) < 3e10);
    } catch {}
    return true;
  }

  shouldShow() {
    if (!this.mayShow()) return false;
    if (this.dataset.showTo === 'region') return Boolean(this.api.shouldShowBanner?.());
    const consent = this.api.currentVisitorConsent?.() || {};
    return CATEGORIES.every((category) => !consent[category]);
  }

  loadDogImages() {
    this.querySelectorAll('image[data-href]').forEach((image) => {
      image.setAttribute('href', image.dataset.href);
      image.removeAttribute('data-href');
    });
  }

  async show(animate) {
    this.loadDogImages();
    if (animate && this.animated) {
      try {
        const dog = await this.dogModule();
        this.wrap.hidden = false;
        this.keepFocusVisible();
        return dog.walkIn(this);
      } catch (error) {
        console.warn('[lasura-cookie-banner] dog module failed, showing plainly', error);
      }
    }
    this.wrap.hidden = false;
    this.keepFocusVisible();
  }

  /** The dog's mood and aside. */
  mood(name, quip) {
    if (!this.walker) return;
    this.walker.classList.remove('mood-up', 'mood-query', 'mood-down', 'mood-happy');
    if (name) this.walker.classList.add(`mood-${name}`);
    if (quip && this.quip) {
      this.quip.textContent = quip;
      this.quip.classList.add('is-on');
    } else {
      this.quip?.classList.remove('is-on');
    }
  }

  /** Focus returns to the opener, else the page. */
  hide(returnTo) {
    const lost = this.wrap.contains(document.activeElement) || document.activeElement === document.body;
    this.wrap.hidden = true;
    if (returnTo?.isConnected && !this.wrap.contains(returnTo)) returnTo.focus({ preventScroll: true });
    else if (lost) {
      const main = document.getElementById('MainContent');
      if (main && !main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1');
      main?.focus({ preventScroll: true });
    }
    this.card.inert = false;
    [this.card, this.walker].forEach((element) => element?.getAnimations().forEach((animation) => animation.cancel()));
    this.walker?.classList.remove('is-walking');
    this.mood(null);
    this.clearScrollPadding();
  }

  /** WCAG 2.4.11: focused elements stay clear of the banner. */
  keepFocusVisible() {
    this.resizeObserver = new ResizeObserver(() => {
      if (!this.wrap.hidden) document.documentElement.style.scrollPaddingBottom = `${this.wrap.offsetHeight + 24}px`;
    });
    this.resizeObserver.observe(this.wrap);
  }

  clearScrollPadding() {
    this.resizeObserver?.disconnect();
    document.documentElement.style.removeProperty('scroll-padding-bottom');
  }

  openPreferences(opener) {
    this.opener = opener;
    const consent = this.api?.currentVisitorConsent?.() || {};
    this.switches.forEach((input) => (input.checked = consent[input.dataset.category] === 'yes'));
    this.wagByChoices();
    this.loadDogImages();
    if (!this.prefs.open) this.prefs.showModal();
  }

  wagByChoices() {
    const on = this.switches.filter((input) => input.checked).length;
    this.prefsDog?.style.setProperty('--lasura-wag', WAG_BY_CHOICES[on]);
  }

  async decide(kind) {
    const choice = {};
    this.switches.forEach((input) => {
      choice[input.dataset.category] = kind === 'accept' ? true : kind === 'reject' ? false : input.checked;
    });
    if (this.api?.saleOfDataRegion?.()) choice.sale_of_data = choice.marketing;
    if (this.api && !this.designMode) {
      try {
        localStorage.setItem(CHOSEN, Date.now());
      } catch {}
      this.api.setTrackingConsent(choice, (result) => {
        if (!result?.error) return document.dispatchEvent(new CustomEvent('lasura:consent-chosen'));
        console.warn('[lasura-cookie-banner] consent not saved', result.error);
        try {
          localStorage.removeItem(CHOSEN);
        } catch {}
      });
    }
    const returnTo = this.opener;
    if (this.prefs.open) {
      this.opener = null;
      this.prefs.close();
    }
    this.busy = true;
    if (!this.wrap.hidden && this.animated) {
      try {
        // A stalled exit doesn't stall the banner.
        const walkOff = (await this.dogModule()).walkOff(this, kind, this.dataset[`done${titleCase(kind)}`]);
        await Promise.race([walkOff, wait(WALK_OFF_LIMIT)]);
      } catch (error) {
        console.warn('[lasura-cookie-banner] dog module failed', error);
      }
    }
    this.hide(returnTo);
    this.busy = false;
    this.toast(this.dataset[`toast${titleCase(kind)}`]);
  }

  async toast(message) {
    if (!message) return;
    this.toastEl.textContent = '';
    await wait(60);
    this.toastEl.textContent = message;
    this.toastEl.classList.add('is-on');
    clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => {
      this.toastEl.classList.remove('is-on');
      setTimeout(() => (this.toastEl.textContent = ''), 400);
    }, 4200);
  }

  onClick(event) {
    const button = event.target.closest('[data-action]');
    if (!button || this.busy) return;
    const action = button.dataset.action;
    if (action === 'preferences') this.openPreferences(button);
    else if (action === 'close') this.prefs.close();
    else this.decide(action);
  }

  onDocumentClick(event) {
    const trigger = event.target.closest('a[href$="#cookie-settings"], [data-lasura-cookie-settings]');
    if (!trigger) return;
    event.preventDefault();
    this.openPreferences(trigger);
  }
}

if (!customElements.get('lasura-cookie-banner')) customElements.define('lasura-cookie-banner', LasuraCookieBanner);
